import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, MapPin, Phone } from "lucide-react";
import { Eyebrow, Reveal } from "../components/Reveal";
import { ADDRESS, PHONE, PHONE_HREF, PROJECT_TYPES } from "../data/site";
import { getSource, trackLead } from "../lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  project_type: "",
  location: "",
  description: "",
  contact_method: "either",
  photo_link: "",
};

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quote-requests`, { ...form, source: getSource() });
      trackLead();
      setDone(true);
      toast.success("Estimate request received. We'll be in touch.");
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-warm pt-20">
      <section className="section-pad">
        <div className="container-cando">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="h-display mt-6 max-w-3xl text-charcoal">Let's Talk About Your Project.</h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="lead mt-6 max-w-xl text-mute">
              Tell us what you're planning and we'll help you take the next step.
            </p>
          </Reveal>

          <div className="mt-16 grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              {done ? (
                <div data-testid="estimate-success" className="border border-bronze/40 bg-warm2 p-10">
                  <CheckCircle2 size={36} className="text-bronze" />
                  <h2 className="h3 mt-5 text-charcoal">Request received.</h2>
                  <p className="mt-3 max-w-md text-[16px] leading-relaxed text-mute">
                    Thanks, {form.name.split(" ")[0] || "there"}. We've received your project details and
                    will follow up using your preferred contact method.
                  </p>
                  <button
                    data-testid="send-another-button"
                    onClick={() => { setForm(EMPTY); setDone(false); }}
                    className="btn btn-outline mt-8"
                  >
                    Send Another Request
                  </button>
                </div>
              ) : (
                <Reveal delay={0.2}>
                  <form onSubmit={submit} data-testid="estimate-form" className="grid gap-7 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="project_type">What are you looking for? *</label>
                      <select
                        id="project_type"
                        data-testid="field-project-type"
                        required
                        value={form.project_type}
                        onChange={set("project_type")}
                        className="field"
                      >
                        <option value="" disabled>Select a project type</option>
                        {PROJECT_TYPES.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="field-label" htmlFor="name">Name *</label>
                      <input id="name" data-testid="field-name" required className="field" value={form.name} onChange={set("name")} placeholder="Your name" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="phone">Phone *</label>
                      <input id="phone" data-testid="field-phone" required type="tel" className="field" value={form.phone} onChange={set("phone")} placeholder="(780) 000-0000" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="email">Email *</label>
                      <input id="email" data-testid="field-email" required type="email" className="field" value={form.email} onChange={set("email")} placeholder="you@example.com" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="location">Project location</label>
                      <input id="location" data-testid="field-location" className="field" value={form.location} onChange={set("location")} placeholder="Neighbourhood, Edmonton" />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="description">Tell us about your project *</label>
                      <textarea
                        id="description"
                        data-testid="field-description"
                        required
                        rows={5}
                        className="field resize-none"
                        value={form.description}
                        onChange={set("description")}
                        placeholder="What are you planning? Approximate size, timeline, anything that helps."
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <span className="field-label">Preferred contact method</span>
                      <div className="flex gap-3" data-testid="field-contact-method">
                        {["phone", "email", "either"].map((m) => (
                          <button
                            key={m}
                            type="button"
                            data-testid={`contact-method-${m}`}
                            onClick={() => setForm((f) => ({ ...f, contact_method: m }))}
                            className={`flex-1 border px-4 py-3 font-heading text-[13px] font-bold uppercase tracking-[0.16em] transition-colors duration-300 ${
                              form.contact_method === m
                                ? "border-charcoal bg-charcoal text-warm"
                                : "border-charcoal/25 text-ink hover:border-charcoal"
                            }`}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="field-label" htmlFor="photo_link">Project photos (optional)</label>
                      <input
                        id="photo_link"
                        data-testid="field-photo-link"
                        className="field"
                        value={form.photo_link}
                        onChange={set("photo_link")}
                        placeholder="Link to photos (Google Drive, Dropbox...)"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <button type="submit" data-testid="submit-estimate-button" disabled={sending} className="btn btn-primary group w-full disabled:opacity-60 sm:w-auto">
                        {sending ? "Sending..." : "Request a Project Estimate"}
                        {!sending && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
                      </button>
                    </div>
                  </form>
                </Reveal>
              )}
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.25}>
                <aside className="grain relative bg-charcoal p-10 text-warm lg:p-12" data-testid="contact-aside">
                  <p className="font-heading text-[13px] font-bold uppercase tracking-[0.24em] text-bronze">
                    Cando Construction Services Ltd.
                  </p>
                  <div className="mt-8 space-y-6 text-[16px]">
                    <p className="flex items-start gap-4">
                      <MapPin size={18} className="mt-1 shrink-0 text-bronze" />
                      <span className="text-warm/75">{ADDRESS}</span>
                    </p>
                    <a href={PHONE_HREF} data-testid="contact-phone" className="flex items-center gap-4 transition-colors hover:text-bronze">
                      <Phone size={18} className="shrink-0 text-bronze" />
                      <span>{PHONE}</span>
                    </a>
                  </div>
                  <div className="mt-10 border-t border-warm/15 pt-8">
                    <p className="text-[15px] leading-relaxed text-warm/60">
                      Framing, renovations and additions for Edmonton homeowners. Every request is
                      reviewed directly by our team.
                    </p>
                  </div>
                </aside>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
